import { stringify } from 'qs';

import type {
  NavigationGuardWithThis,
  RouteLocationNormalizedGeneric,
  Router,
} from 'vue-router';

import { login } from '@eaphone/auth';
import { isAndroid, isInWechat } from '@eaphone/env';
import { Toast } from '@eaphone/modal';
import { captureException } from '@eaphone/sentry';
import { Auth, sessionStore } from '@eaphone/storage';

import { wxsdk } from './api.ts';

function wechatUrl(url: string, scope: string) {
  const params = stringify({
    appid: import.meta.env.APP_ID,
    redirect_uri: url,
    response_type: 'code',
    scope: `snsapi_${scope}`,
    state: '',
  });

  return `https://open.weixin.qq.com/connect/oauth2/authorize?${params}#wechat_redirect`;
}

function fullUrl(url: string) {
  return new URL(
    /* globals __webpack_public_path__: readonly */ __webpack_public_path__ +
      url.replace(/^\//, ''),
    window.location.origin,
  ).href;
}

function replace({
  path,
  query,
}: {
  path: string;
  query: Record<string, any>;
}) {
  const io = stringify(query);
  window.location.replace(`${fullUrl(path)}?${io}`);
}

async function loginLogic(to: RouteLocationNormalizedGeneric) {
  Toast.loading('正在登录...');
  const {
    path,
    query: { code, state, ...query },
  } = to;

  return login({ code: code as string, type: 'weixin' })
    .then(() => {
      Toast.close();

      if (isAndroid) {
        return { path, query, replace: true };
      }

      replace({ path, query });

      return false;
    })
    .catch((error) => {
      console.error(error);
      Toast.fail(error.message);

      return { name: 'error-401' };
    });
}

export const loginHook: NavigationGuardWithThis<Router> = async (to, _from) => {
  if (to.meta.auth) {
    if (to.query.code) {
      return loginLogic(to);
    }

    if (Auth.token) {
      // 已有 token
      if (
        to.meta.needPhone !== false &&
        to.name !== 'phone' &&
        sessionStore.has('need-phone')
      ) {
        sessionStore.set('phone-bind-from', to.fullPath);

        return { name: 'phone', replace: true };
      }

      return;
    }

    if (isInWechat) {
      const url = wechatUrl(
        fullUrl(to.fullPath),
        to.meta.wechat?.baseAuth ? 'base' : 'userinfo',
      );
      window.location.replace(url);

      return false;
    }

    return { name: 'error-sdk', replace: true };
  }
};

export { usePageConfig } from './mixin.ts';

const logo = new URL(
  // eslint-disable-next-line unicorn/relative-url-style
  '@xinxiangsui/design-resource/商标/心相随/favicon.png',
  import.meta.url,
).href;

type ShareData = Parameters<(typeof wxsdk)['updateAppMessageShareData']>[0];

const shareInfo: ShareData = {
  title: '心相随微信公众号',
  desc: '心相随，守护您的健康',
  link: window.location.origin,
  imgUrl: logo,
  success() {},
};

interface Sdk {
  updateTimelineShareData(info?: Partial<ShareData>): void;
  updateAppMessageShareData(info?: Partial<ShareData>): void;
}

const sdk: Sdk = {
  updateTimelineShareData(info?: ShareData) {
    wxsdk.updateTimelineShareData({ ...shareInfo, ...info });
  },
  updateAppMessageShareData(info?: ShareData) {
    wxsdk.updateAppMessageShareData({ ...shareInfo, ...info });
  },
};

interface WeixinOpenTagsErrorEvent extends Event {
  detail: {
    errMsg: string;
    [key: string]: any;
  };
}

document.addEventListener(
  'WeixinOpenTagsError',
  (event: unknown) => {
    const errEvent = event as WeixinOpenTagsErrorEvent;
    captureException(new Error(errEvent.detail.errMsg), {
      tags: {
        scene: 'WeixinOpenTagsError',
      },
      contexts: {
        detail: errEvent.detail,
      },
    });
  },
  false,
);

export function sdkHook() {
  return new Promise<Sdk>((resolve, reject) => {
    wxsdk.ready(() => {
      resolve(sdk);
    });
    wxsdk.error((error) => {
      captureException(error, {
        tags: { wechat: 'jssdk' },
      });
      reject(new Error(error.errMsg));
    });
  });
}
